/**
 * 画像アップロードアダプター（統一インターフェース）
 *
 * 全ての環境で、バックエンドにFormDataで画像を送信する。
 * 環境に応じた保存先（Cloudinary/ローカル）の切り替えはバックエンド側で行う。
 */

// API URL設定
const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};

/**
 * プロフィール画像アップロード
 * @param {Blob} imageBlob - 画像データ
 * @returns {Promise<{url: string, publicId?: string}>}
 */
export const uploadProfileImage = async (imageBlob) => {
  const formData = new FormData();
  formData.append('profile-image', imageBlob, 'profile.jpg');

  const token = localStorage.getItem('token');
  const API_URL = getApiUrl();

  const response = await fetch(`${API_URL}/api/user/profile-image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const errorResponse = await response.json();
    throw new Error(errorResponse.message || '画像のアップロードに失敗しました。');
  }

  const data = await response.json();
  return { url: data.image_url || data.imageUrl || data.url };
};

/**
 * ストア画像アップロード
 * @param {string} storeId - ストアID
 * @param {Blob} imageBlob - 画像データ
 * @returns {Promise<{url: string, publicId?: string}>}
 */
export const uploadStoreImage = async (storeId, imageBlob) => {
  const formData = new FormData();
  formData.append('image', imageBlob, 'store.jpg');

  const token = localStorage.getItem('token');
  const API_URL = getApiUrl();

  const response = await fetch(`${API_URL}/api/store/${storeId}/image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const errorResponse = await response.json();
    throw new Error(errorResponse.message || '画像のアップロードに失敗しました。');
  }

  const data = await response.json();
  return { url: data.image_url || data.imageUrl || data.url };
};

/**
 * 商品画像アップロード
 * @param {string} itemId - 商品ID
 * @param {Blob} imageBlob - 画像データ
 * @returns {Promise<{url: string, publicId?: string}>}
 */
export const uploadItemImage = async (itemId, imageBlob) => {
  const formData = new FormData();
  formData.append('image', imageBlob, 'item.jpg');

  const token = localStorage.getItem('token');
  const API_URL = getApiUrl();

  const response = await fetch(`${API_URL}/api/store/items/${itemId}/image`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const errorResponse = await response.json();
    throw new Error(errorResponse.message || '画像のアップロードに失敗しました。');
  }

  const data = await response.json();
  return { url: data.image_url || data.imageUrl || data.url };
};

/**
 * プロジェクト画像アップロード
 * @param {string} projectId - プロジェクトID
 * @param {Blob} imageBlob - 画像データ
 * @returns {Promise<{url: string, publicId?: string}>}
 */
export const uploadProjectImage = async (projectId, imageBlob) => {
  const formData = new FormData();
  formData.append('image', imageBlob, 'project.jpg');

  const token = localStorage.getItem('token');
  const API_URL = getApiUrl();

  const response = await fetch(`${API_URL}/api/projects/${projectId}/image`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const errorResponse = await response.json();
    throw new Error(errorResponse.message || '画像のアップロードに失敗しました。');
  }

  const data = await response.json();
  return { url: data.imageUrl || data.image_url || data.url };
};
